import { defineStore } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { getQuestionsInfoFetch, getExamQuestionFetch, submitAnswerFetch, finishExamFetch, getExamResultFetch } from '@services/testService';

export const useTestRenderStore = defineStore('useTestRenderStore', () => {
    const toast = useToast();
    const { t } = useI18n({ useScope: 'global' });
    const route = useRoute();
    const router = useRouter();

    const questionsInfo = ref([]);
    const testConfig = ref({});
    const question = ref({});
    const currentQuestionId = ref(route.query.question || null);
    const role = route.query.role;

    const selectedQuestion = computed(() => questionsInfo.value.find((q) => q.id == currentQuestionId.value));

    const getQuestionsInfo = async (id) => {
        try {
            const result = await getQuestionsInfoFetch(id);
            questionsInfo.value = result.ids;
            testConfig.value = result.config;
            if (questionsInfo.value.length) {
                currentQuestionId.value = route.query.question || questionsInfo.value[0].id;
            }
        } catch (e) {
            if (e.response.data.message == 'ExamFinished') {
                router.replace({ path: '/result', query: { exam_id: id } });
            } else {
                toast.add({ severity: 'error', summary: t('tests.errors.' + e.response.data.message), life: 3000 });
            }
        }
    };

    const getExamQuestion = async (id) => {
        try {
            question.value = await getExamQuestionFetch(id);
            currentQuestionId.value = id;
            router.push({ query: { ...route.query, question: id } });
        } catch (e) {
            toast.add({ severity: 'error', summary: t('tests.errors.' + e.response.data.message), life: 3000 });
        }
    };

    const submitAnswer = async (answers) => {
        try {
            const data = await submitAnswerFetch({ id: currentQuestionId.value, ...answers });

            if (data == 'Success') {
                selectedQuestion.value.checked = true;
                goToNextQuestion();
            } else {
                if (question.value.type === 3) {
                    question.value = {
                        ...question.value,
                        ...data,
                    };
                } else {
                    question.value.answers = data;
                }
                selectedQuestion.value.isCorrect = isUserAnswerCorrect(question.value, data);
            }
        } catch (e) {
            toast.add({ severity: 'error', summary: t('tests.errors.' + e.response.data.message), life: 3000 });
        }
    };

    const goToPreviousQuestion = () => {
        const index = questionsInfo.value.findIndex((q) => q.id === currentQuestionId.value);
        if (index > 0) getExamQuestion(questionsInfo.value[index - 1].id);
    };

    const goToNextQuestion = () => {
        const index = questionsInfo.value.findIndex((q) => q.id === currentQuestionId.value);
        if (index < questionsInfo.value.length - 1) getExamQuestion(questionsInfo.value[index + 1].id);
    };

    const isUserAnswerCorrect = (question, answers) => {
        const { type, isMultiple } = question;

        if (type === 1 || type === 2) {
            const correctAnswers = answers.filter((a) => a.isCorrect);
            const selectedAnswers = answers.filter((a) => a.checked);
            if (isMultiple) {
                if (correctAnswers.length !== selectedAnswers.length) return false;
                return selectedAnswers.every((sa) => correctAnswers.some((ca) => ca.id === sa.id));
            }
            const selected = selectedAnswers[0];
            return selected && selected.isCorrect;
        }
        if (type === 3) {
            return answers?.isCorrect === true;
        }
        if (type === 4 || type === 5) {
            return Array.isArray(answers) && answers.every((a) => a.isCorrect);
        }

        return false;
    };

    const finishExam = async (id) => {
        try {
            const data = await finishExamFetch({ id });
            if (data == 'Success') {
                toast.add({ severity: 'success', summary: t('tests.successExam'), life: 3000 });
                if (role == 'admin') {
                    router.replace({ path: '/result', query: { exam_id: id } });
                } else {
                    const origins = ['http://localhost:5173/', 'https://dev.proctoring.uz/', 'https://proctoring.uz/', 'https://192.168.0.151:5173/', 'https://dashboard.univora.uz/', 'https://proctoring.univora.uz/', 'https://app.proctoring.uz/'];

                    origins.forEach((origin) => {
                        window.parent.postMessage({ testIsFinish: true }, origin);
                    });
                }
            }
        } catch (e) {
            toast.add({ severity: 'error', summary: t('tests.errors.' + e.response.data.message), life: 3000 });
        }
    };

    const getExamResult = async (id) => {
        try {
            const data = await getExamResultFetch(id);
            return data;
        } catch (e) {
            toast.add({ severity: 'error', summary: t('tests.errors.' + e.response.data.message), life: 3000 });
        }
    };

    return {
        getQuestionsInfo,
        questionsInfo,
        testConfig,
        question,
        currentQuestionId,
        getExamQuestion,
        submitAnswer,
        finishExam,
        getExamResult,
        goToPreviousQuestion,
        goToNextQuestion,
        selectedQuestion,
    };
});
